import { useCallback, useEffect, useState } from 'react';

// Web Serial weight scale (Chrome/Edge only).
// A serial port can only be opened once per tab, so the connection lives here as a
// module-level singleton shared by every component that needs it.
const BAUD_RATE = 9600;
const DATA_BITS = 7;
const STOP_BITS = 1;
const PARITY = 'none';
const START_COMMAND = 'SIR';

interface SerialPortLike {
    open(options: { baudRate: number; dataBits?: number; stopBits?: number; parity?: string; flowControl?: string }): Promise<void>;
    close(): Promise<void>;
    readable: ReadableStream<Uint8Array> | null;
    writable: WritableStream<Uint8Array> | null;
}

interface SerialLike {
    getPorts(): Promise<SerialPortLike[]>;
    requestPort(): Promise<SerialPortLike>;
}

declare global {
    interface Navigator {
        serial?: SerialLike;
    }
}

type Listener = (rawLine: string) => void;

let port: SerialPortLike | null = null;
let reader: ReadableStreamDefaultReader<string> | null = null;
let busy = false;
const readingListeners = new Set<Listener>();
const statusListeners = new Set<(connected: boolean) => void>();

export const isScaleSupported = () => typeof navigator !== 'undefined' && !!navigator.serial;

const log = (...args: unknown[]) => console.log('[scale]', ...args);
const logError = (...args: unknown[]) => console.error('[scale]', ...args);

const setConnected = (value: boolean) => statusListeners.forEach((fn) => fn(value));

// Releases the port so a later connect can reopen it; without the close() the browser
// keeps it open and the next open() throws InvalidStateError.
const cleanup = async () => {
    const closing = port;
    reader = null;
    port = null;
    if (closing) {
        try {
            await closing.close();
        } catch (err) {
            logError('failed to close port (already gone?):', err);
        }
    }
    log('disconnected');
    setConnected(false);
};

// Reads whatever the scale streams and forwards each complete line.
const readLoop = async () => {
    let buffer = '';
    while (reader) {
        try {
            const { value, done } = await reader.read();
            if (done) {
                log('stream closed by device');
                break;
            }
            if (!value) continue;

            buffer += value;
            let i;
            while ((i = buffer.search(/[\r\n]/)) !== -1) {
                const line = buffer.slice(0, i).trim();
                buffer = buffer.slice(i + 1);
                if (line) {
                    log('received:', JSON.stringify(line), `(${readingListeners.size} listener(s))`);
                    readingListeners.forEach((fn) => fn(line));
                }
            }
        } catch (err) {
            logError('read error (scale unplugged?):', err);
            break;
        }
    }
    await cleanup();
};

const PORT_OPTIONS = { baudRate: BAUD_RATE, dataBits: DATA_BITS, stopBits: STOP_BITS, parity: PARITY, flowControl: 'none' };

const openPort = async (selected: SerialPortLike) => {
    log('opening port', PORT_OPTIONS);
    try {
        await selected.open(PORT_OPTIONS);
    } catch (err) {
        // Recover from a port left half-open by a previous session/tab.
        logError('open failed, closing and retrying once:', err);
        try { await selected.close(); } catch { /* was not open */ }
        await selected.open(PORT_OPTIONS);
    }

    const decoder = new TextDecoderStream();
    selected.readable!.pipeTo(decoder.writable as WritableStream<Uint8Array>).catch(() => { /* closed */ });
    reader = decoder.readable.getReader();
    port = selected;
    log('connected');
    setConnected(true);

    // Ask the scale to stream weights continuously; harmless if it ignores the command.
    try {
        const encoder = new TextEncoderStream();
        encoder.readable.pipeTo(selected.writable!).catch(() => { /* closed */ });
        const writer = encoder.writable.getWriter();
        await writer.write(`${START_COMMAND}\r\n`);
        writer.releaseLock();
        log('sent start command:', START_COMMAND);
    } catch (err) {
        logError('failed to send start command (device may still stream on its own):', err);
    }

    readLoop();
};

/** Reconnects to an already-approved port. Never prompts. */
export const reconnectScale = async (): Promise<boolean> => {
    if (!isScaleSupported()) {
        log('Web Serial not supported in this browser (needs Chrome/Edge over https or localhost)');
        return false;
    }
    if (port) return true;
    if (busy) return false;
    busy = true;
    try {
        const [approved] = await navigator.serial!.getPorts();
        if (!approved) {
            log('no previously approved port — use "Connect Scale" in the menu once');
            return false;
        }
        log('found previously approved port, reconnecting silently');
        await openPort(approved);
        return true;
    } catch (err) {
        logError('silent reconnect failed:', err);
        return false;
    } finally {
        busy = false;
    }
};

/** Opens the browser port picker. Must be called straight from a click handler. */
export const connectScale = async (): Promise<boolean> => {
    if (!isScaleSupported()) {
        log('Web Serial not supported in this browser (needs Chrome/Edge over https or localhost)');
        return false;
    }
    if (port) return true;
    if (busy) return false;
    busy = true;
    try {
        log('opening browser port picker…');
        const selected = await navigator.serial!.requestPort();
        await openPort(selected);
        return true;
    } catch (err) {
        logError('port selection cancelled or connect failed:', err);
        return false;
    } finally {
        busy = false;
    }
};

/** React state for the connection, plus a connect() for the menu button. */
export function useWeightScale() {
    const [connected, setConnected] = useState(!!port);

    useEffect(() => {
        const listener = (value: boolean) => setConnected(value);
        statusListeners.add(listener);
        // Reattach to a previously approved port after a reload.
        reconnectScale();
        return () => { statusListeners.delete(listener); };
    }, []);

    const connect = useCallback(async () => (await reconnectScale()) || (await connectScale()), []);

    return { isSupported: isScaleSupported(), connected, connect };
}

/** Subscribes to live scale readings while `enabled` is true. */
export function useScaleReadings(enabled: boolean, onReading: Listener) {
    useEffect(() => {
        if (!enabled) return;
        readingListeners.add(onReading);
        return () => { readingListeners.delete(onReading); };
    }, [enabled, onReading]);
}
